/** Дашборд: что происходит прямо сейчас и что требует внимания. */
import {
  Badge, Card, Countdown, Empty, Skeleton, fmtDay, num, plural, relativeDay,
} from '@allswipes/ui';
import { Link } from 'react-router-dom';

import { api } from '../api';
import { Layout } from '../components/Layout';
import { useResource } from '../hooks';
import { useConsole } from '../session';

export function DashboardScreen() {
  const { me } = useConsole();
  const campaigns = useResource(() => api.consoleCampaigns(), []);
  const claims = useResource(() => api.consoleClaims(['unclaimed', 'processing']), []);

  const needsDraw = (campaigns.data?.campaigns ?? []).filter((c) => c.status === 'ended' && c.draw_at);

  return (
    <Layout
      title="Дашборд"
      subtitle={me ? `${me.brand.name} · ${me.brand.regions.join(' · ')}` : undefined}
      action={
        <Link to="/create" className="btn pri">
          Создать акцию
        </Link>
      }
    >
      {/* Блокировку партнёр узнаёт здесь, а не из отказа при запуске акции:
          платформа обязана сказать прямо, что она сделала и почему. */}
      {me?.brand.blocked_at && (
        <Card style={{ marginBottom: 18 }}>
          <div className="h2">Акции бренда приостановлены платформой</div>
          <p className="sub" style={{ marginTop: 6 }}>
            {me.brand.blocked_reason || 'Причина не указана.'} Новые акции не запускаются, коды
            не принимаются. Уже выданные призы и обязательства по ним остаются за брендом —
            очередь выдачи работает как обычно. Напиши нам, когда вопрос будет решён.
          </p>
        </Card>
      )}

      {me && (
        <div className="stats" style={{ marginBottom: 22 }}>
          <Stat value={num(me.stats.active)} label="активных акций" />
          <Stat value={num(me.stats.campaigns)} label="всего акций" />
          <Stat value={num(me.stats.participants)} label="участников" />
          <Stat value={num(me.stats.entries)} label="кодов принято" />
          <Stat value={num(me.stats.prizes_given)} label="призов выдано" />
          <Stat value={num(me.stats.pending_claims)} label="призов в очереди" />
          {/* Рейтинг — то же число, что покупатель видит на карточке бренда.
              Партнёр должен видеть его там же, где остальные показатели, а не
              искать в отдельном разделе. */}
          <Stat
            value={ratingValue(me.brand.rating?.value ?? 0)}
            label={
              me.brand.rating?.count
                ? `рейтинг · ${num(me.brand.rating.count)} ${plural(me.brand.rating.count, 'отзыв', 'отзыва', 'отзывов')}`
                : 'рейтинг · отзывов нет'
            }
          />
        </div>
      )}

      {needsDraw.length > 0 && (
        <Card style={{ marginBottom: 18, borderColor: 'var(--border-2)' }}>
          <div className="row" style={{ marginBottom: 8 }}>
            <span className="h2">Ждут розыгрыша</span>
            <span className="spacer" />
            <Badge tone="warn">нужен публичный сид</Badge>
          </div>
          <p className="sub" style={{ marginBottom: 12 }}>
            Приём кодов закрыт, список билетов зафиксирован. Розыгрыш не запускается сам: нужен
            внешний публичный сид — его вводит человек, который может за него поручиться.
          </p>
          {needsDraw.map((c) => (
            <div className="krow" key={c.id}>
              <span className="k">
                {c.name}
                <span className="faint" style={{ display: 'block', fontSize: 11 }}>
                  {num(c.tickets)} билетов · розыгрыш {relativeDay(c.draw_at)}
                </span>
              </span>
              <span className="v">
                <Link to={`/campaigns/${c.id}`} className="btn pri sm">
                  Провести
                </Link>
              </span>
            </div>
          ))}
        </Card>
      )}

      <div className="grid c2">
        <Card>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="h2">Акции</span>
            <span className="spacer" />
            <Link to="/campaigns" className="faint" style={{ fontSize: 12 }}>
              Все →
            </Link>
          </div>
          {campaigns.loading && <Skeleton count={3} height={48} />}
          {campaigns.data?.campaigns.slice(0, 6).map((c) => (
            <Link to={`/campaigns/${c.id}`} className="krow" key={c.id} style={{ display: 'flex' }}>
              <span className="k">
                {c.name}
                <span className="faint" style={{ display: 'block', fontSize: 11 }}>
                  {statusLabel(c.status)} · {fmtDay(c.start_at)}
                </span>
              </span>
              <span className="v mono">{num(c.participants)}</span>
            </Link>
          ))}
          {campaigns.data?.campaigns.length === 0 && (
            <Empty title="Пока нет акций">
              <Link to="/create" className="btn pri" style={{ marginTop: 12 }}>
                Создать первую
              </Link>
            </Empty>
          )}
        </Card>

        <Card>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="h2">Призы к выдаче</span>
            <span className="spacer" />
            <Link to="/claims" className="faint" style={{ fontSize: 12 }}>
              Все →
            </Link>
          </div>
          {claims.loading && <Skeleton count={3} height={44} />}
          {claims.data?.claims.slice(0, 6).map((claim) => (
            <div className="krow" key={claim.id}>
              <span className="k">
                {claim.prize_label}
                <span className="faint mono" style={{ display: 'block', fontSize: 11 }}>
                  {claim.ticket_number ?? 'мгновенный приз'}
                </span>
              </span>
              <span className="v">
                {claim.deadline_at ? <Countdown to={claim.deadline_at} /> : claim.status}
              </span>
            </div>
          ))}
          {claims.data?.claims.length === 0 && <Empty title="Очередь пуста" />}
        </Card>
      </div>
    </Layout>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="stat">
      <div className="v">{value}</div>
      <div className="l">{label}</div>
    </div>
  );
}

export function statusLabel(status: string): string {
  switch (status) {
    case 'draft':
      return 'черновик';
    case 'scheduled':
      return 'запланирована';
    case 'active':
      return 'идёт';
    case 'ended':
      return 'приём закрыт';
    case 'drawn':
      return 'розыгрыш проведён';
    default:
      return status;
  }
}

/** Средняя оценка ×100 как «4,2». Пока отзывов нет — прочерк, а не ноль. */
function ratingValue(value: number): string {
  if (value === 0) return '—';
  const whole = Math.floor(value / 100);
  const frac = Math.round((value % 100) / 10);
  return frac > 0 ? `${whole},${frac}` : String(whole);
}
